import { OTHER_KEY, rankLines } from "./palette";
import type { CostIndexLine, CostIndexPoint } from "./types";

/**
 * C7 v1.4: index chart forecast values share the chart rows under a `forecast:` prefix, drawn dashed, never in a billed
 * key. Money chart forecast months are their own rows with quality `forecast`, under the component keys.
 */
export const FORECAST_PREFIX = "forecast:";
export const FORECAST_TOTAL_KEY = "forecast:total_index";
/** Money chart only: a forecast month's bars are lighter than a carried month's and outlined with a dash. */
export const FORECAST_FILL_OPACITY = 0.35;
export const FORECAST_DASH = "5 4";
/** One shared empty default, so a chart without a forecast keeps its memoised rows. */
export const NO_FORECAST: CostIndexPoint[] = [];

type ChartRow = { month: string; quality: string } & Record<string, string | number | null>;

/**
 * Lines ranked on the billed series only, so a billed line's order, colour and fold never depend on the forecast; a
 * component that only the forecast brings is appended after them (`added`), so it still gets a colour.
 */
export function rankWithForecast(series: CostIndexPoint[], forecast: CostIndexPoint[]): { shown: CostIndexLine[]; folded: CostIndexLine[]; added: CostIndexLine[] } {
  const ranked = rankLines(series);
  if (forecast.length === 0) return { ...ranked, added: [] };
  const known = new Set([...ranked.shown, ...ranked.folded].map((line) => line.key));
  const added = [...new Map(forecast.flatMap((point) => point.lines).filter((line) => !known.has(line.key)).map((line) => [line.key, line])).values()];
  return { shown: [...ranked.shown, ...added], folded: ranked.folded, added };
}

/**
 * Index chart rows with the forecast appended. The last billed row also carries every forecast key, so the dashed
 * segment starts where the billed line ends; a forecast row carries null in every billed key. With no forecast the
 * rows are returned as they are.
 */
export function indexRowsWithForecast<T extends ChartRow>(rows: T[], forecast: CostIndexPoint[], lineKeys: string[]): ChartRow[] {
  if (forecast.length === 0 || rows.length === 0) return rows;
  const last = rows[rows.length - 1];
  const join: ChartRow = {
    ...last,
    [FORECAST_TOTAL_KEY]: last.total_index ?? null,
    ...Object.fromEntries(lineKeys.map((key) => [`${FORECAST_PREFIX}${key}`, last[key] ?? null])),
  };
  const blank = Object.fromEntries(Object.keys(last).filter((key) => key !== "month" && key !== "quality").map((key) => [key, null]));
  const appended: ChartRow[] = forecast.map((point) => ({
    ...blank,
    month: point.month,
    quality: "forecast",
    carried_sku_count: 0,
    [FORECAST_TOTAL_KEY]: point.total_index == null ? null : Number(point.total_index),
    ...Object.fromEntries(lineKeys.map((key) => {
      const line = point.lines.find((item) => item.key === key);
      return [`${FORECAST_PREFIX}${key}`, line?.index == null ? null : Number(line.index)];
    })),
  }));
  return [...rows.slice(0, -1), join, ...appended];
}

/** Money chart rows for forecast points, folded exactly as the billed rows are; only the last one carries the total label. */
export function moneyForecastRows(forecast: CostIndexPoint[], folded: Set<string>, totalLabel: (value: string | null) => string): ChartRow[] {
  return forecast.map((point, index) => {
    const row: ChartRow = {
      month: point.month, quality: "forecast", carried_sku_count: 0,
      total_label: index === forecast.length - 1 ? totalLabel(point.total_unit_cost) : "",
      total: point.total_unit_cost == null ? null : Number(point.total_unit_cost),
    };
    let other = 0; let hasOther = false;
    for (const line of point.lines) {
      if (line.unit_cost == null) continue;
      if (folded.has(line.key)) { other += Number(line.unit_cost); hasOther = true; }
      else row[line.key] = Number(line.unit_cost);
    }
    if (hasOther) row[OTHER_KEY] = other;
    return row;
  });
}

/** Every index value the chart draws, billed and forecast: C7 v1.1.1's round grid must hold the dashed segment too. */
export function drawnIndexValues(rows: ChartRow[], lineKeys: string[]): Array<number | null | undefined> {
  return rows.flatMap((row) => [row.total_index, row[FORECAST_TOTAL_KEY], ...lineKeys.flatMap((key) => [row[key], row[`${FORECAST_PREFIX}${key}`]])] as Array<number | null | undefined>);
}
